import getProperty from 'lodash-es/get';
import setProperty from 'lodash-es/set';
import i18n from '../i18n/i18n'

/**
 * Validační funkce generovaná funkcí requireFields.
 *
 * @callback requireFieldsValidator
 * @param {Object} values - formulářové hodnoty pro zvalidování
 * @param {Object} [errors={}] - objekt pro zápis chyb polí
 * @return {Object} objekt se zapsanými chybami polí
 */

/**
 * Vytvoří validační funkci povinných polí formuláře.
 * Výslednou funkci lze použít v rámci validace přes redux-form.
 *
 * Použití:
 * ```
 * // Konstrukce validároru
 * const validator = requireFields(['jmeno', 'adresa.ulice']);
 *
 * // 1. Způsob použití
 * const errors = validator(formValues);
 *
 * // 2. Způsob použití
 * const errors = {};
 * validator(formValues, errors);
 * ```
 *
 * Použití (konfigurační volby):
 * ```
 * const validator = requireFields(['jmeno', 'adresa.ulice'], {
 *      errorMessage: 'Moje vlastní chybová zpráva',
 *      trimStrings: true, // Řetězce obsahující pouze mezery budou reportovány jako chyby
 * });
 * ```
 *
 * @param {string[]} paths - seznam názvů polí, které chceme validovat. Lze uvéset i otečkované cesty
 *                           pro specifikaci polí v zanořených datových strukturách, např. 'subjekt.adresa.ulice'
 *                           zvaliduje vyplněnost pole ve struktuře `{subjekt: {adresa: {ulice: ???}}}`
 * @param {Object} [options] - volitelné parametry
 * @param {string} [options.errorMessage] - volitelná chybová zpráva, použitá pro nevyplněná povinná pole
 * @param {boolean} [options.trimStrings=false] - příznak, zda string hodnoty trimovat, tzn. zda je
 *                                                string obsahující poze mezery považován za validační chybu
 * @returns {requireFieldsValidator} výsledná validační funkce
 */
export default function requireFields(paths, options = {}) {
    if (typeof options === 'string') {
        options = {errorMessage: options}; // Zpětná kompatibilita, druhý parametr dříve představoval errorMessage
    }

    const {
        errorMessage = i18n('global.form.title.error.required'),
        trimStrings = false,
    } = options;

    return (values, errors = {}) =>  {
        for (const path of paths) {
            let value = getProperty(values, path);

            if (trimStrings && typeof value === 'string') {
                value = value.trim();
            }

            // Podmínka nesmí být !value, protože chceme akceptovat číselnou hodnotu 0 a boolean hodnotu false
            if (value == null || value === '') {
                setProperty(errors, path, errorMessage);
            }
        }
        return errors;
    }
}
